import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "contact-section relative py-20 md:py-32 bg-gradient-to-b from-[#1E2652] to-[#1E2652]/95" }
const _hoisted_2 = { class: "container relative z-10 mx-auto px-4 md:px-6 max-w-5xl" }
const _hoisted_3 = { class: "text-center mb-12" }
const _hoisted_4 = { class: "bg-white/5 backdrop-blur-sm rounded-xl p-6 md:p-8 space-y-8" }
const _hoisted_5 = { class: "contact-info" }
const _hoisted_6 = { class: "text-gray-300 space-y-4" }
const _hoisted_7 = { class: "flex items-center space-x-4" }
const _hoisted_8 = { class: "contact-form" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 0,
  class: "text-green-400 text-sm text-center"
}
const _hoisted_11 = {
  key: 1,
  class: "text-red-400 text-sm text-center"
}

import { ref, onMounted } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { Mail } from 'lucide-vue-next'
import { collection, addDoc, getFirestore } from 'firebase/firestore'
import { db } from '../firebase/config'


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactPage',
  setup(__props) {

gsap.registerPlugin(ScrollTrigger)

const titleRef = ref<HTMLElement | null>(null)
const subtitleRef = ref<HTMLElement | null>(null)

const form = ref({
  name: '',
  email: '',
  message: ''
})

const isSubmitting = ref(false)
const submitStatus = ref<'idle' | 'success' | 'error'>('idle')

const submitForm = async () => {
  console.log('Form submission started')
  console.log('Form data:', form.value)
  
  if (!form.value.name || !form.value.email || !form.value.message) {
    console.log('Form validation failed - missing required fields')
    return
  }
  
  console.log('Form validation passed, proceeding with submission')
  isSubmitting.value = true
  submitStatus.value = 'idle'
  
  try {
    console.log('1. Starting form submission...')
    
    // Ensure Firebase is initialized
    if (!db) {
      console.error('Firebase db is not initialized')
      throw new Error('Firebase is not initialized')
    }

    // Double check Firestore instance
    const firestore = getFirestore()
    if (!firestore) {
      throw new Error('Firestore instance not available')
    }
    
    console.log('2. Creating messages collection reference...')
    const messagesCollection = collection(firestore, 'messages')
    
    console.log('3. Preparing document data...')
    const docData = {
      to: 'contact@padelgo.ma',
      message: {
        subject: `${form.value.name} - ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
        html: `
          <h2>Nouveau message de contact</h2>
          <h4><strong>Nom:</strong> ${form.value.name}</h4>
          <h4><strong>Email:</strong> ${form.value.email}</h4>
          <h4><strong>Message:</strong></h4>
          <p>${form.value.message}</p>
        `,
        text: `
          Nouveau message de contact
          Nom: ${form.value.name}
          Email: ${form.value.email}
          Message: ${form.value.message}
        `
      },
      createdAt: new Date()
    }
    
    console.log('4. Attempting to add document...')
    try {
      const docRef = await addDoc(messagesCollection, docData)
      console.log('5. Document added successfully with ID:', docRef.id)
      
      submitStatus.value = 'success'
      form.value = {
        name: '',
        email: '',
        message: ''
      }
    } catch (firestoreError) {
      console.error('Firestore error:', firestoreError)
      if (firestoreError instanceof Error) {
        if (firestoreError.message.includes('permission-denied')) {
          throw new Error('Permission denied: Please check Firebase security rules')
        } else if (firestoreError.message.includes('not-found')) {
          throw new Error('Firestore collection not found')
        } else {
          throw new Error(`Firestore error: ${firestoreError.message}`)
        }
      }
      throw firestoreError
    }
  } catch (error) {
    console.error('Error in form submission:', error)
    if (error instanceof Error) {
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      })
    }
    submitStatus.value = 'error'
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  // Subtle fade-in animations
  if (titleRef.value) {
    gsap.from(titleRef.value, {
      y: 20,
      opacity: 0,
      duration: 0.8,
      ease: 'power2.out'
    })
  }

  if (subtitleRef.value) {
    gsap.from(subtitleRef.value, {
      opacity: 0,
      duration: 0.8,
      delay: 0.2,
      ease: 'power2.out'
    })
  }

  const sections = document.querySelectorAll('.contact-info, .contact-form')
  sections.forEach((section, index) => {
    gsap.from(section, {
      opacity: 0,
      y: 20,
      duration: 0.8,
      delay: 0.1 * index,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: section,
        start: 'top 90%'
      }
    })
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "absolute inset-0" }, [
      _createElementVNode("div", { class: "absolute inset-0 bg-grid opacity-5" })
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h1", {
          ref_key: "titleRef",
          ref: titleRef,
          class: "text-3xl md:text-4xl font-bold text-white mb-4"
        }, " Contactez-nous ", 512),
        _createElementVNode("p", {
          ref_key: "subtitleRef",
          ref: subtitleRef,
          class: "text-lg text-gray-400"
        }, " Vous avez des questions, des suggestions ou besoin d'assistance ? N'hésitez pas à nous contacter. ", 512)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("section", _hoisted_5, [
          _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "text-xl md:text-2xl font-semibold text-[#B7E000] mb-4" }, " Nos Coordonnées ", -1)),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_unref(Mail), { class: "w-6 h-6 text-[#B7E000]" }),
              _cache[3] || (_cache[3] = _createElementVNode("a", {
                href: "mailto:contact@padelgo.ma",
                class: "hover:text-[#B7E000] transition-colors"
              }, "contact@padelgo.ma", -1))
            ]),
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex items-center space-x-4" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-[#B7E000]",
                fill: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" })
              ]),
              _createElementVNode("a", {
                href: "https://wa.me/33766151241",
                class: "hover:text-[#B7E000] transition-colors"
              }, "+33 (0) 7 66 15 12 41")
            ], -1))
          ])
        ]),
        _createElementVNode("section", _hoisted_8, [
          _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "text-xl md:text-2xl font-semibold text-[#B7E000] mb-4" }, " Envoyez-nous un message ", -1)),
          _createElementVNode("form", {
            onSubmit: _withModifiers(submitForm, ["prevent"]),
            class: "space-y-4"
          }, [
            _createElementVNode("div", null, [
              _cache[6] || (_cache[6] = _createElementVNode("label", {
                for: "name",
                class: "block text-[#B7E000] mb-2"
              }, "Nom Complet", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                id: "name",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.name) = $event)),
                class: "w-full px-3 py-2 bg-white/10 border border-[#B7E000]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B7E000] text-white",
                placeholder: "Votre nom",
                required: ""
              }, null, 512), [
                [_vModelText, form.value.name]
              ])
            ]),
            _createElementVNode("div", null, [
              _cache[7] || (_cache[7] = _createElementVNode("label", {
                for: "email",
                class: "block text-[#B7E000] mb-2"
              }, "Email", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "email",
                id: "email",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.email) = $event)),
                class: "w-full px-3 py-2 bg-white/10 border border-[#B7E000]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B7E000] text-white",
                placeholder: "email@exemple.com",
                required: ""
              }, null, 512), [
                [_vModelText, form.value.email]
              ])
            ]),
            _createElementVNode("div", null, [
              _cache[8] || (_cache[8] = _createElementVNode("label", {
                for: "message",
                class: "block text-[#B7E000] mb-2"
              }, "Message", -1)),
              _withDirectives(_createElementVNode("textarea", {
                id: "message",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.message) = $event)),
                rows: "4",
                class: "w-full px-3 py-2 bg-white/10 border border-[#B7E000]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B7E000] text-white",
                placeholder: "Votre message...",
                required: ""
              }, null, 512), [
                [_vModelText, form.value.message]
              ])
            ]),
            _createElementVNode("button", {
              type: "submit",
              class: "btn-primary w-full",
              disabled: isSubmitting.value
            }, _toDisplayString(isSubmitting.value ? 'Envoi en cours...' : 'Envoyer le message'), 9, _hoisted_9),
            (submitStatus.value === 'success')
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, " Merci pour votre message ! Nous vous répondrons dans les plus brefs délais. "))
              : _createCommentVNode("", true),
            (submitStatus.value === 'error')
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, " Une erreur est survenue. Veuillez réessayer. "))
              : _createCommentVNode("", true)
          ], 32)
        ])
      ])
    ])
  ]))
}
}

})